'use client';

import { Combobox } from '@base-ui-components/react/combobox';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

// Root — single-select combobox whose retained value we ignore; navigation
// happens via each item's anchor. `filter={null}` disables internal filtering
// (results arrive already server-filtered). Generic over the hit value type so
// the engine stays domain-agnostic.
function SearchCombobox<Value>(props: Combobox.Root.Props<Value>) {
  return <Combobox.Root<Value> filter={null} {...props} />;
}

// The styled search bar. The input itself opens the popup (openOnInputClick),
// so this is a plain styled <form role="search">; the consumer wires onSubmit.
// Horizontal padding belongs to the input rather than to the bar, so the input
// spans the bar edge to edge: the popup anchors to the input, and an input inset
// from the bar centres the popup off the bar's centre.
function SearchComboboxForm({ className, ...props }: ComponentProps<'form'>) {
  return (
    <form
      role="search"
      data-slot="search-combobox-form"
      className={clsxm(
        'bg-background-intense ring-foreground-muted/50 relative z-(--z-max) flex h-10 w-full items-center rounded-md ring',
        'lap:h-14 lap:max-w-[744px]',
        className
      )}
      {...props}
    />
  );
}

// 16px at every width: iOS Safari zooms the page in on focusing anything
// smaller, which leaves the layout wider than the screen.
function SearchComboboxInput({ className, ...props }: Combobox.Input.Props) {
  return (
    <Combobox.Input
      type="search"
      data-slot="search-combobox-input"
      autoComplete="off"
      className={clsxm(
        'h-full w-full appearance-none bg-transparent text-base outline-none',
        'lap:pr-18 lap:pl-5 pr-12 pl-3',
        'placeholder:text-foreground/50 focus:placeholder:text-transparent',
        '[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden',
        '[&::-webkit-search-results-button]:hidden [&::-webkit-search-results-decoration]:hidden',
        className
      )}
      {...props}
    />
  );
}

function SearchComboboxSubmit({
  className,
  children,
  onClick,
  ...props
}: ComponentProps<'button'>) {
  return (
    <button
      type="submit"
      data-slot="search-combobox-submit"
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
      className={clsxm(
        'lap:pr-5 lap:pl-4 absolute inset-y-0 right-0 flex items-center rounded-md pr-3 pl-2 outline-none',
        'focus-visible:ring-foreground-muted/50 focus-visible:ring-2',
        className
      )}
      {...props}
    >
      {children ?? <MagnifyingGlassIcon className="text-foreground lap:w-8 w-6 shrink-0" />}
    </button>
  );
}

function SearchComboboxPortal(props: Combobox.Portal.Props) {
  return <Combobox.Portal {...props} />;
}

function SearchComboboxBackdrop({ className, ...props }: Combobox.Backdrop.Props) {
  return (
    <Combobox.Backdrop
      data-slot="search-combobox-backdrop"
      className={clsxm(
        'fixed inset-0 z-[calc(var(--z-max)-1)] bg-black/50',
        'data-[state=closed]:animate-out data-[state=open]:animate-in',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className
      )}
      {...props}
    />
  );
}

function SearchComboboxPositioner({ className, ...props }: Combobox.Positioner.Props) {
  return (
    <Combobox.Positioner
      side="bottom"
      align="center"
      sideOffset={10}
      data-slot="search-combobox-positioner"
      className={clsxm('z-(--z-max)', className)}
      {...props}
    />
  );
}

function SearchComboboxArrow({ className, ...props }: Combobox.Arrow.Props) {
  return (
    <Combobox.Arrow
      data-slot="search-combobox-arrow"
      className={clsxm('bg-background -top-1 size-3 rotate-45', className)}
      {...props}
    />
  );
}

// The popup is capped to the height the positioner reports as free below the
// anchor. Floating UI measures `--available-height` against the visual viewport,
// so an on-screen keyboard is already subtracted from it; subtracting a keyboard
// inset again here collapses the popup to nothing. The cap is what makes the
// list inside it scroll rather than run off the screen.
function SearchComboboxContent({ className, ...props }: Combobox.Popup.Props) {
  return (
    <Combobox.Popup
      initialFocus={false}
      data-slot="search-combobox-content"
      className={clsxm(
        'bg-background relative flex w-[calc(100vw-2rem)] max-w-240 flex-col overflow-hidden rounded-md shadow-xl outline-none',
        'max-h-(--available-height)',
        'data-[state=closed]:animate-out data-[state=open]:animate-in',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className
      )}
      {...props}
    />
  );
}

// Combobox has no Close part; this is a plain button whose onClick flips the
// consumer's controlled `open` state.
function SearchComboboxClose({ className, children, ...props }: ComponentProps<'button'>) {
  return (
    <button
      type="button"
      data-slot="search-combobox-close"
      className={clsxm(
        'text-foreground hover:bg-background-intense lap:top-4 lap:right-4 absolute top-2 right-2 z-10 inline-flex h-8 w-8 items-center justify-center rounded-md',
        className
      )}
      {...props}
    >
      {children ?? <XMarkIcon className="w-5 shrink-0" />}
    </button>
  );
}

// The grid container = the listbox, and the popup's scroll port: `min-h-0` lets
// it shrink below its content inside the capped flex column, and
// `overscroll-contain` keeps a flick at either end off the page behind the
// backdrop.
function SearchComboboxList({ className, ...props }: Combobox.List.Props) {
  return (
    <Combobox.List
      data-slot="search-combobox-list"
      className={clsxm(
        'lap:grid-cols-4 grid grid-cols-1 gap-6',
        'lap:pt-6 p-5 pt-10',
        'lap:divide-y-0 lap:divide-x divide-foreground/10 divide-y',
        'min-h-0 overflow-y-auto overscroll-contain',
        className
      )}
      {...props}
    />
  );
}

function SearchComboboxGroup({ className, ...props }: Combobox.Group.Props) {
  return (
    <Combobox.Group
      data-slot="search-combobox-group"
      className={clsxm('min-w-0', className)}
      {...props}
    />
  );
}

function SearchComboboxGroupLabel({ className, ...props }: Combobox.GroupLabel.Props) {
  return (
    <Combobox.GroupLabel
      data-slot="search-combobox-group-label"
      className={clsxm('font-heading mb-3 text-lg tracking-tight', className)}
      {...props}
    />
  );
}

function SearchComboboxItem({ className, ...props }: Combobox.Item.Props) {
  return (
    <Combobox.Item
      data-slot="search-combobox-item"
      className={clsxm('outline-none', className)}
      {...props}
    />
  );
}

// Presentational helpers reused by the default (empty-input) panel, which is
// not part of the option list. Kept for layout parity with the old family.
function SearchSection({ className, ...props }: ComponentProps<'section'>) {
  return <section data-slot="search-section" className={clsxm('min-w-0', className)} {...props} />;
}

function SearchSectionSpacer({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      aria-hidden
      data-slot="search-section-spacer"
      className={clsxm('lap:h-auto lap:w-6 h-6 w-full shrink-0', className)}
      {...props}
    />
  );
}

function SearchSectionHeading({ className, ...props }: ComponentProps<'h3'>) {
  return (
    <h3
      data-slot="search-section-heading"
      className={clsxm('font-heading mb-3 text-lg tracking-tight', className)}
      {...props}
    />
  );
}

export {
  SearchCombobox,
  SearchComboboxForm,
  SearchComboboxInput,
  SearchComboboxSubmit,
  SearchComboboxPortal,
  SearchComboboxBackdrop,
  SearchComboboxPositioner,
  SearchComboboxArrow,
  SearchComboboxContent,
  SearchComboboxClose,
  SearchComboboxList,
  SearchComboboxGroup,
  SearchComboboxGroupLabel,
  SearchComboboxItem,
  SearchSection,
  SearchSectionSpacer,
  SearchSectionHeading,
};
